import React, { useState } from 'react';

import { Modal, Button, useToast } from '../../design/components';
import { deleteRecipe } from '@recipe-rack/shared';
import type { Recipe } from '@recipe-rack/shared';

import { db } from './Firebase/InitialiseFirebase';

interface DeleteRecipeModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after the recipe is successfully deleted (e.g. to close the drawer). */
  onDeleted: () => void;
  userId: string;
  recipe: Recipe;
}

/**
 * Confirms deletion of a recipe. On confirm: calls `deleteRecipe`,
 * closes the modal and drawer (via `onDeleted`), and shows a toast.
 */
const DeleteRecipeModal: React.FC<DeleteRecipeModalProps> = ({
  open,
  onClose,
  onDeleted,
  userId,
  recipe,
}) => {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await deleteRecipe(db, userId, recipe.recipeId);
      toast.show('Recipe deleted', 'success');
      onClose();
      onDeleted();
    } catch (err) {
      console.error('Failed to delete recipe', err);
      toast.show('Could not delete recipe', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete recipe?"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="tomato"
            onClick={handleConfirm}
            loading={deleting}
          >
            Delete
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
        <strong style={{ color: 'var(--ink)' }}>{recipe.title}</strong>{' '}
        will be permanently removed.
      </p>
    </Modal>
  );
};

export default DeleteRecipeModal;
